import { BaseComponent } from '../lib/BaseComponent.js';
import { api, Location } from '../lib/api.js';
import { router } from '../lib/router.js';
import { buildAppNav, APP_NAV_STYLES } from '../lib/app-nav.js';

/**
 * Location List Page Component
 * Enhanced list view with search, filtering, and sorting
 * Includes delete confirmation modal
 */
export class LocationListPage extends BaseComponent {
  private isLoading = true;
  private locations: Location[] = [];
  private filteredLocations: Location[] = [];
  private errorMessage = '';

  // Extinguisher counts per location
  private extinguisherCounts: Map<string, number> = new Map();

  // Sort state
  private sortBy: string = 'name';
  private sortDirection: 'asc' | 'desc' = 'asc';

  // Search state
  private searchQuery: string = '';

  // Delete modal state
  private showDeleteModal = false;
  private locationToDelete: Location | null = null;

  protected async render(): Promise<void> {
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'list-container' });

    // Header with navigation
    const header = this.createElement('header', { className: 'list-header' });
    const headerContent = this.createElement('div', { className: 'header-content' });

    const logo = this.createElement('h1', { className: 'logo' }, 'FireGuard');
    headerContent.appendChild(logo);

    const nav = buildAppNav((t, p, txt) => this.createElement(t as any, p as any, txt as any), '/locations', () => this.handleLogout());
    headerContent.appendChild(nav);

    header.appendChild(headerContent);
    container.appendChild(header);

    // Main content
    const main = this.createElement('main', { className: 'list-main' });

    // Page title and actions
    const titleBar = this.createElement('div', { className: 'title-bar' });
    const title = this.createElement('h2', {}, 'All Locations');
    const addBtn = this.createElement('button', { className: 'btn-primary' }, '+ Add Location');
    addBtn.addEventListener('click', () => router.navigate('/locations/new'));
    titleBar.appendChild(title);
    titleBar.appendChild(addBtn);
    main.appendChild(titleBar);

    // Loading state
    if (this.isLoading) {
      const loading = this.createElement('div', { className: 'loading' }, 'Loading locations...');
      main.appendChild(loading);
      container.appendChild(main);
      this.shadow.appendChild(container);

      // Fetch data
      await this.loadLocations();
      return;
    }

    // Error state
    if (this.errorMessage) {
      const error = this.createElement('div', { className: 'error-message' }, this.errorMessage);
      main.appendChild(error);
      container.appendChild(main);
      this.shadow.appendChild(container);
      return;
    }

    // Controls: Search and Sort
    // Sorting an empty list and three counters reading zero are noise in front
    // of someone who has nothing yet. Show them once there is something to
    // sort and count.
    const hasAny = this.locations.length > 0;
    if (hasAny) {
      main.appendChild(this.createControlsSection());
      main.appendChild(this.createStatsSection());
    }

    // Empty state
    if (this.filteredLocations.length === 0) {
      const empty = this.createElement('div', { className: 'empty-state' });
      const emptyIcon = this.createElement('div', { className: 'empty-icon' }, '📍');
      const emptyTitle = this.createElement('h3', {}, this.locations.length === 0 ? 'No locations yet' : 'No matching locations');
      const emptyText = this.createElement('p', {}, this.locations.length === 0 ? 'Add your first location to get started' : 'Try adjusting your search');
      empty.appendChild(emptyIcon);
      empty.appendChild(emptyTitle);
      empty.appendChild(emptyText);
      main.appendChild(empty);
      container.appendChild(main);
      this.shadow.appendChild(container);
      return;
    }

    // Locations table
    const table = this.createLocationsTable();
    main.appendChild(table);

    container.appendChild(main);
    this.shadow.appendChild(container);

    // Delete confirmation modal
    if (this.showDeleteModal) {
      const modal = this.createDeleteModal();
      container.appendChild(modal);
    }
  }

  private createControlsSection(): HTMLElement {
    const controls = this.createElement('div', { className: 'controls' });

    // Search bar
    const searchContainer = this.createElement('div', { className: 'search-container' });
    const searchInput = this.createElement('input', {
      type: 'text',
      placeholder: 'Search by name, address, city, or state...',
      className: 'search-input',
    }) as HTMLInputElement;
    searchInput.value = this.searchQuery;
    searchInput.addEventListener('input', (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.applyFilters();
    });
    searchContainer.appendChild(searchInput);
    controls.appendChild(searchContainer);

    // Sort controls
    const sortControls = this.createElement('div', { className: 'sort-controls' });
    const sortLabel = this.createElement('span', { className: 'sort-label' }, 'Sort by:');
    sortControls.appendChild(sortLabel);

    const sortSelect = this.createElement('select', { className: 'sort-select' }) as HTMLSelectElement;
    sortSelect.addEventListener('change', (e) => {
      this.sortBy = (e.target as HTMLSelectElement).value;
      this.applyFilters();
    });
    [
      { value: 'name', label: 'Name' },
      { value: 'city', label: 'City' },
      { value: 'state', label: 'State' },
      { value: 'zip', label: 'Zip Code' },
      { value: 'created_at', label: 'Date Added' },
    ].forEach(({ value, label }) => {
      const option = this.createElement('option', { value }, label) as HTMLOptionElement;
      if (value === this.sortBy) {
        option.selected = true;
      }
      sortSelect.appendChild(option);
    });
    sortControls.appendChild(sortSelect);

    const sortDirBtn = this.createElement('button', { className: 'sort-dir-btn' }, this.sortDirection === 'asc' ? '↑' : '↓');
    sortDirBtn.addEventListener('click', () => {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      sortDirBtn.textContent = this.sortDirection === 'asc' ? '↑' : '↓';
      this.applyFilters();
    });
    sortControls.appendChild(sortDirBtn);

    controls.appendChild(sortControls);

    return controls;
  }

  private createStatsSection(): HTMLElement {
    const stats = this.createElement('div', { className: 'stats' });

    const total = this.filteredLocations.length;
    const totalExtinguishers = Array.from(this.extinguisherCounts.values()).reduce((sum, count) => sum + count, 0);

    // Group by state
    const states = new Set(this.filteredLocations.map(l => l.state));
    const stateCount = states.size;

    const statItems = [
      { label: 'Total Locations', value: total, className: 'stat-total' },
      { label: 'Total Extinguishers', value: totalExtinguishers, className: 'stat-extinguishers' },
      { label: 'States/Regions', value: stateCount, className: 'stat-states' },
    ];

    statItems.forEach(({ label, value, className }) => {
      const statCard = this.createElement('div', { className: `stat-card ${className}` });
      const statValue = this.createElement('div', { className: 'stat-value' }, value.toString());
      const statLabel = this.createElement('div', { className: 'stat-label' }, label);
      statCard.appendChild(statValue);
      statCard.appendChild(statLabel);
      stats.appendChild(statCard);
    });

    return stats;
  }

  private createLocationsTable(): HTMLElement {
    const tableContainer = this.createElement('div', { className: 'table-container' });
    const table = this.createElement('table', { className: 'locations-table' });

    // Header
    const thead = this.createElement('thead', {});
    const headerRow = this.createElement('tr', {});
    ['Name', 'Address', 'City', 'State', 'Zip', 'Extinguishers', 'Actions'].forEach(text => {
      const th = this.createElement('th', {}, text);
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    // Body
    const tbody = this.createElement('tbody', {});
    this.filteredLocations.forEach(location => {
      const row = this.createElement('tr', { className: 'location-row' });

      // Name (clickable)
      const nameCell = this.createElement('td', { className: 'cell-name' });
      const nameLink = this.createElement('a', {
        href: `/locations/${location.id}`,
        className: 'name-link',
      }, location.name);
      nameLink.addEventListener('click', (e) => {
        e.preventDefault();
        router.navigate(`/locations/${location.id}`);
      });
      nameCell.appendChild(nameLink);
      row.appendChild(nameCell);

      // Address
      const addressCell = this.createElement('td', {}, location.address);
      row.appendChild(addressCell);

      // City
      const cityCell = this.createElement('td', {}, location.city);
      row.appendChild(cityCell);

      // State
      const stateCell = this.createElement('td', {}, location.state);
      row.appendChild(stateCell);

      // Zip
      const zipCell = this.createElement('td', {}, location.zip);
      row.appendChild(zipCell);

      // Extinguisher count
      const extCountCell = this.createElement('td', { className: 'cell-count' });
      const count = this.extinguisherCounts.get(location.id) || 0;
      const countBadge = this.createElement('span', { className: 'count-badge' }, count.toString());
      extCountCell.appendChild(countBadge);
      row.appendChild(extCountCell);

      // Actions
      const actionsCell = this.createElement('td', { className: 'cell-actions' });
      const viewBtn = this.createElement('button', { className: 'btn-link' }, 'View');
      viewBtn.addEventListener('click', () => router.navigate(`/locations/${location.id}`));
      const editBtn = this.createElement('button', { className: 'btn-link' }, 'Edit');
      editBtn.addEventListener('click', () => router.navigate(`/locations/${location.id}?edit=true`));
      const deleteBtn = this.createElement('button', { className: 'btn-link btn-danger' }, 'Delete');
      deleteBtn.addEventListener('click', () => this.showDeleteConfirmation(location));
      actionsCell.appendChild(viewBtn);
      actionsCell.appendChild(editBtn);
      actionsCell.appendChild(deleteBtn);
      row.appendChild(actionsCell);

      tbody.appendChild(row);
    });
    table.appendChild(tbody);

    tableContainer.appendChild(table);
    return tableContainer;
  }

  private createDeleteModal(): HTMLElement {
    const modal = this.createElement('div', { className: 'modal-overlay' });
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.hideDeleteModal();
      }
    });

    const modalContent = this.createElement('div', { className: 'modal-content' });

    const modalHeader = this.createElement('div', { className: 'modal-header' });
    const modalTitle = this.createElement('h3', {}, 'Delete Location');
    modalHeader.appendChild(modalTitle);
    modalContent.appendChild(modalHeader);

    const modalBody = this.createElement('div', { className: 'modal-body' });
    const warningIcon = this.createElement('div', { className: 'warning-icon' }, '⚠️');
    modalBody.appendChild(warningIcon);

    if (this.locationToDelete) {
      const extCount = this.extinguisherCounts.get(this.locationToDelete.id) || 0;

      const message = this.createElement('p', {},
        `Are you sure you want to delete "${this.locationToDelete.name}"?`
      );
      modalBody.appendChild(message);

      if (extCount > 0) {
        const warning = this.createElement('p', { className: 'warning-text' },
          `Warning: This location has ${extCount} fire extinguisher${extCount > 1 ? 's' : ''}. Deleting this location may affect extinguisher records.`
        );
        modalBody.appendChild(warning);
      }

      const note = this.createElement('p', { className: 'note-text' },
        'This action cannot be undone.'
      );
      modalBody.appendChild(note);
    }
    modalContent.appendChild(modalBody);

    const modalFooter = this.createElement('div', { className: 'modal-footer' });
    const cancelBtn = this.createElement('button', { className: 'btn-secondary' }, 'Cancel');
    cancelBtn.addEventListener('click', () => this.hideDeleteModal());
    const confirmBtn = this.createElement('button', { className: 'btn-danger' }, 'Delete Location');
    confirmBtn.addEventListener('click', () => this.handleDelete());
    modalFooter.appendChild(cancelBtn);
    modalFooter.appendChild(confirmBtn);
    modalContent.appendChild(modalFooter);

    modal.appendChild(modalContent);
    return modal;
  }

  private showDeleteConfirmation(location: Location): void {
    this.locationToDelete = location;
    this.showDeleteModal = true;
    this.shadow.innerHTML = '';
    this.render();
  }

  private hideDeleteModal(): void {
    this.locationToDelete = null;
    this.showDeleteModal = false;
    this.shadow.innerHTML = '';
    this.render();
  }

  private async handleDelete(): Promise<void> {
    if (!this.locationToDelete) return;

    try {
      await api.deleteLocation(this.locationToDelete.id);

      // Remove from local state
      this.locations = this.locations.filter(l => l.id !== this.locationToDelete!.id);
      this.extinguisherCounts.delete(this.locationToDelete!.id);

      // Hide modal and refresh
      this.locationToDelete = null;
      this.showDeleteModal = false;
      this.applyFilters();
    } catch (error) {
      this.errorMessage = (error as Error).message || 'Failed to delete location';
      this.hideDeleteModal();
    }
  }

  private applyFilters(): void {
    let filtered = [...this.locations];

    // Apply search
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(l =>
        l.name.toLowerCase().includes(query) ||
        l.address.toLowerCase().includes(query) ||
        l.city.toLowerCase().includes(query) ||
        l.state.toLowerCase().includes(query) ||
        l.zip.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal: any = a[this.sortBy as keyof Location];
      let bVal: any = b[this.sortBy as keyof Location];

      // Handle null values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      if (typeof aVal === 'string') {
        return this.sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return this.sortDirection === 'asc'
        ? aVal - bVal
        : bVal - aVal;
    });

    this.filteredLocations = filtered;
    this.shadow.innerHTML = '';
    this.render();
  }

  private async loadLocations(): Promise<void> {
    try {
      // Load locations
      const response = await api.getLocations();
      this.locations = response.locations;
      this.filteredLocations = [...this.locations];

      // Load extinguisher counts for each location
      await this.loadExtinguisherCounts();

      this.isLoading = false;
      this.shadow.innerHTML = '';
      await this.render();
    } catch (error) {
      this.isLoading = false;
      this.errorMessage = (error as Error).message || 'Failed to load locations';
      this.shadow.innerHTML = '';
      await this.render();
    }
  }

  private async loadExtinguisherCounts(): Promise<void> {
    try {
      // Fetch all extinguishers to count by location
      const response = await api.getExtinguishers();
      const extinguishers = response.extinguishers;

      // Count extinguishers per location
      const counts = new Map<string, number>();
      extinguishers.forEach(ext => {
        if (ext.location_id) {
          counts.set(ext.location_id, (counts.get(ext.location_id) || 0) + 1);
        }
      });

      this.extinguisherCounts = counts;
    } catch (error) {
      console.error('Failed to load extinguisher counts:', error);
      // Non-critical error, continue without counts
    }
  }

  private handleLogout(): void {
    api.logout();
    router.navigate('/login');
  }

  private getStyles(): string {
    return `
      ${APP_NAV_STYLES}

      .list-container {
        min-height: 100vh;
        background: var(--gray-50);
      }

      .list-header {
        background: white;
        border-bottom: 1px solid var(--gray-200);
        padding: var(--spacing-md) var(--spacing-lg);
      }

      .header-content {
        max-width: 1400px;
        margin: 0 auto;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .logo {
        font-size: 24px;
        font-weight: 700;
        color: var(--danger);
        margin: 0;
      }

      .nav {
        display: flex;
        gap: var(--spacing-md);
      }

      .list-main {
        max-width: 1400px;
        margin: 0 auto;
        padding: var(--spacing-lg);
      }

      .title-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--spacing-lg);
      }

      .title-bar h2 {
        font-size: 28px;
        font-weight: 600;
        color: var(--gray-900);
      }

      .btn-primary {
        padding: 12px 24px;
        background: var(--primary);
        color: white;
        border: none;
        border-radius: var(--border-radius);
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
      }

      .btn-primary:hover {
        background: var(--primary-dark);
      }

      .btn-secondary {
        padding: 10px 20px;
        background: white;
        color: var(--gray-700);
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
      }

      .btn-secondary:hover {
        background: var(--gray-50);
      }

      .btn-link {
        background: transparent;
        border: none;
        color: var(--primary);
        cursor: pointer;
        font-size: 14px;
        padding: 4px 8px;
        text-decoration: underline;
      }

      .btn-link:hover {
        color: var(--primary-dark);
      }

      .btn-danger {
        color: var(--danger);
      }

      .btn-danger:hover {
        color: #dc2626;
      }

      .loading {
        text-align: center;
        padding: 40px;
        color: var(--gray-500);
      }

      .error-message {
        background: #fef2f2;
        border: 1px solid #fecaca;
        color: #dc2626;
        padding: var(--spacing-md);
        border-radius: var(--border-radius);
        margin-bottom: var(--spacing-md);
      }

      .empty-state {
        text-align: center;
        padding: 60px 20px;
        background: white;
        border-radius: var(--border-radius);
        border: 2px dashed var(--gray-300);
      }

      .empty-icon {
        font-size: 64px;
        margin-bottom: var(--spacing-md);
      }

      .empty-state h3 {
        font-size: 20px;
        color: var(--gray-900);
        margin-bottom: var(--spacing-sm);
      }

      .empty-state p {
        color: var(--gray-500);
      }

      .controls {
        background: white;
        border-radius: var(--border-radius);
        padding: var(--spacing-lg);
        margin-bottom: var(--spacing-lg);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .search-container {
        margin-bottom: var(--spacing-md);
      }

      .search-input {
        width: 100%;
        padding: 12px;
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 16px;
      }

      .search-input:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      .sort-controls {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
      }

      .sort-label {
        font-size: 14px;
        color: var(--gray-700);
        font-weight: 500;
      }

      .sort-select {
        padding: 8px 12px;
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 14px;
        background: white;
        cursor: pointer;
      }

      .sort-dir-btn {
        padding: 8px 12px;
        background: white;
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        cursor: pointer;
        font-size: 16px;
      }

      .sort-dir-btn:hover {
        background: var(--gray-50);
      }

      .stats {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: var(--spacing-md);
        margin-bottom: var(--spacing-lg);
      }

      .stat-card {
        background: white;
        border-radius: var(--border-radius);
        padding: var(--spacing-lg);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        text-align: center;
      }

      .stat-value {
        font-size: 32px;
        font-weight: 700;
        margin-bottom: var(--spacing-sm);
        color: var(--primary);
      }

      .stat-label {
        font-size: 14px;
        color: var(--gray-500);
      }

      .table-container {
        background: white;
        border-radius: var(--border-radius);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        overflow-x: auto;
      }

      .locations-table {
        width: 100%;
        border-collapse: collapse;
      }

      .locations-table th {
        text-align: left;
        padding: var(--spacing-md);
        border-bottom: 2px solid var(--gray-200);
        font-size: 14px;
        font-weight: 600;
        color: var(--gray-700);
        background: var(--gray-50);
      }

      .locations-table td {
        padding: var(--spacing-md);
        border-bottom: 1px solid var(--gray-200);
        font-size: 14px;
      }

      .location-row:hover {
        background: var(--gray-50);
      }

      .cell-name {
        font-weight: 600;
      }

      .name-link {
        color: var(--primary);
        text-decoration: none;
      }

      .name-link:hover {
        text-decoration: underline;
      }

      .cell-count {
        text-align: center;
      }

      .count-badge {
        display: inline-block;
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 600;
        background: var(--gray-100);
        color: var(--gray-700);
      }

      .cell-actions {
        display: flex;
        gap: var(--spacing-sm);
      }

      /* Modal Styles */
      .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      }

      .modal-content {
        background: white;
        border-radius: var(--border-radius);
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        max-width: 500px;
        width: 90%;
        max-height: 90vh;
        overflow-y: auto;
      }

      .modal-header {
        padding: var(--spacing-lg);
        border-bottom: 1px solid var(--gray-200);
      }

      .modal-header h3 {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        color: var(--gray-900);
      }

      .modal-body {
        padding: var(--spacing-lg);
        text-align: center;
      }

      .warning-icon {
        font-size: 48px;
        margin-bottom: var(--spacing-md);
      }

      .modal-body p {
        margin: 0 0 var(--spacing-md) 0;
        color: var(--gray-700);
        line-height: 1.6;
      }

      .warning-text {
        background: #fef3c7;
        border: 1px solid #fbbf24;
        color: #92400e;
        padding: var(--spacing-md);
        border-radius: var(--border-radius);
        font-weight: 500;
      }

      .note-text {
        font-size: 14px;
        color: var(--gray-500);
        font-style: italic;
      }

      .modal-footer {
        padding: var(--spacing-lg);
        border-top: 1px solid var(--gray-200);
        display: flex;
        justify-content: flex-end;
        gap: var(--spacing-md);
      }

      @media (max-width: 768px) {
        .stats {
          grid-template-columns: 1fr;
        }

        .table-container {
          overflow-x: scroll;
        }

        .nav {
          flex-wrap: wrap;
          gap: var(--spacing-sm);
        }

        .modal-content {
          width: 95%;
        }
      }
    `;
  }
}

// Register custom element
customElements.define('location-list-page', LocationListPage);
